/**
 * @fileoverview ProspectPuffin Privacy Policy registry data.
 *
 * AUTHORED 2026-07-14 (AISEO-001). ProspectPuffin shipped LIVE with no privacy policy and no
 * terms of service at all: `getLegalDocument` had no `prospect` appKey, so /privacy and /terms
 * rendered an internal error — which the MSSG-001 prerender then baked into static HTML and
 * served to crawlers as the page's entire content.
 *
 * ⚠️ PENDING LEGAL REVIEW FOR ACCURACY. Drafted from the platform's documented behaviour
 * (PROSPECT-001, `GUIDES/shared/standards/prospect-platform.md`), not from counsel.
 *
 * ProspectPuffin's data practices differ MATERIALLY from every sibling product, and the
 * differences are the parts that need a lawyer's eye:
 *
 *   - It is the only Colony product that processes personal data about people who are NOT the
 *     customer's employees and have NO relationship with the customer — business contacts at
 *     third-party companies, assembled from public building-permit records and public business
 *     listings. That is data-broker-adjacent territory (CCPA/CPRA §1798.99.80 registration,
 *     GDPR Art. 14 "indirect collection" notice duties) and is the single highest-risk item here.
 *   - It sends outbound email and SMS to those third parties, gated by TCPA/DNC checks.
 *   - It submits prospect data to a third-party LLM (Anthropic) for qualification scoring.
 */

/** @type {import('../../types.js').LegalDocument} */
export const privacy = Object.freeze({
  appKey: 'prospect',
  documentKey: 'privacy',
  frontmatter: {
    title: 'Privacy Policy',
    productName: 'ProspectPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-07-14',
    contactEmail: 'privacy@prospectpuffin.com',
    canonicalUrl: 'https://prospectpuffin.com/privacy',
    ogImage: 'https://prospectpuffin.com/og-image.jpg',
    description:
      'ProspectPuffin privacy policy. How we collect, use, and protect business-development, account, and prospect data.',
  },
  sections: [
    {
      id: 'information-we-collect',
      title: '1. Information We Collect',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'When you use {productName}, we collect information you provide directly, including your business name, email address, phone number, location data, and the user account details needed to operate your business-development workspace.',
        },
        {
          type: 'paragraph',
          text: 'We process the customer and job records you choose to synchronize from your own field-service or CRM system — including company names, business contact names, business phone numbers and email addresses, service addresses, and service history. This data originates with you; we act as a processor of it on your behalf.',
        },
        {
          type: 'paragraph',
          text: 'We also ingest information about prospective commercial accounts from public and licensed sources, including public building-permit records published by local jurisdictions and public business-listing data. This information concerns businesses and the business contacts associated with them, and typically includes company name, business address, business contact details, and permit or listing attributes.',
        },
        {
          type: 'paragraph',
          text: 'We automatically collect usage data such as IP addresses, browser type, pages visited, and timestamps to operate the service and ensure its security.',
        },
      ],
    },
    {
      id: 'how-we-use-your-information',
      title: '2. How We Use Your Information',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: 'We use your information to provide business-development services: identifying dormant commercial accounts worth reactivating, scoring and qualifying prospective accounts, assembling worklists, and recording the outreach activity you carry out against them.',
        },
        {
          type: 'paragraph',
          text: 'We may also use your information to send service updates, respond to inquiries, improve our scoring and qualification models, and comply with legal obligations.',
        },
      ],
    },
    {
      id: 'prospect-data-and-its-sources',
      title: '3. Prospect Data and Its Sources',
      order: 3,
      blocks: [
        {
          type: 'paragraph',
          text: 'Information about prospective accounts is obtained from sources OTHER than the individual it concerns — specifically, public records published by government jurisdictions and public business-listing data. We collect it for the legitimate business purpose of business-to-business commercial outreach, and we limit it to business-context information: company identity, business address, business contact details, and publicly published permit or listing attributes.',
        },
        {
          type: 'paragraph',
          text: 'We do not knowingly collect consumer (non-business) personal information from these sources, and we do not attempt to enrich prospect records with data about individuals in their personal capacity.',
        },
        {
          type: 'paragraph',
          text: 'If you are a business contact whose information appears in {productName} and you wish to access, correct, or delete it, or to object to its processing, contact us at {contactEmail} and we will action your request. You do not need an account with us to make such a request.',
        },
      ],
    },
    {
      id: 'automated-qualification',
      title: '4. Automated Qualification and AI Processing',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} scores prospective accounts to help you prioritize outreach. Scoring combines deterministic rules with an automated qualification step that submits prospect record fields to a third-party large-language-model provider (Anthropic) for assessment. Where that provider is unavailable, a deterministic heuristic is used instead.',
        },
        {
          type: 'paragraph',
          text: 'These scores are decision SUPPORT: they rank and filter a worklist that a human reviews. They do not produce legal or similarly significant effects about any individual, and no outreach is sent automatically on the strength of a score alone — promoting a prospect and contacting it are actions a person takes.',
        },
      ],
    },
    {
      id: 'outreach-consent-and-dnc',
      title: '5. Outreach, Consent, and Do-Not-Call',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'Outbound email and SMS sent through {productName} pass a consent and Do-Not-Call gate before dispatch. The gate checks the federal DNC registry, applicable per-state registries, your own internal suppression list, recipient-initiated stop requests, and SMS STOP-keyword enrollments. A recipient who replies STOP is suppressed for that channel.',
        },
        {
          type: 'paragraph',
          text: 'Every send and every block decision is recorded in an append-only activity log, so the basis for contacting any recipient can be reconstructed. You remain the sender of record for outreach you initiate, and you are responsible for ensuring it complies with the law applicable to you.',
        },
      ],
    },
    {
      id: 'data-security',
      title: '6. Data Security',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'We implement industry-standard security measures including encryption in transit (TLS) and at rest, company isolation at the database-schema level, role-based access controls, and audit logging, to protect your account data and the prospect and customer records we process for you.',
        },
      ],
    },
    {
      id: 'data-sharing',
      title: '7. Data Sharing',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'We do not sell your personal information, and we do not sell prospect records. We share data with service providers strictly to operate the platform on your behalf and under confidentiality obligations: with your field-service or CRM provider to synchronize the records you have connected; with an AI provider (Anthropic) to perform qualification scoring; and with email and SMS providers to deliver the outreach you send. We may disclose information where required by law.',
        },
      ],
    },
    {
      id: 'data-retention',
      title: '8. Data Retention',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: 'We retain your data for as long as your account is active or as needed to provide the service. Prospect records that are dismissed, and outreach activity logs, are retained for the period necessary to evidence compliance with outbound-contact rules and to prevent re-contacting a suppressed recipient. On termination we delete or return your data on request, subject to any retention the law requires of us.',
        },
      ],
    },
    {
      id: 'your-rights',
      title: '9. Your Rights',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'You have the right to access, correct, or delete your personal information, to request a copy of your data, and to opt out of non-essential communications. Depending on where you live you may also have the right to object to or restrict processing. To exercise any of these rights, contact us at {contactEmail}.',
        },
      ],
    },
    {
      id: 'cookies-and-analytics',
      title: '10. Cookies and Analytics',
      order: 10,
      blocks: [
        {
          type: 'paragraph',
          text: 'We use cookies and similar technologies to analyze site traffic and improve your experience. You can manage cookie preferences through our cookie consent banner. Analytics data is collected only with your consent.',
        },
      ],
    },
    {
      id: 'changes-to-this-policy',
      title: '11. Changes to This Policy',
      order: 11,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may update this privacy policy from time to time. We will notify you of significant changes by email or through a notice on our website. Continued use of our services after changes constitutes acceptance of the updated policy.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '12. Contact Us',
      order: 12,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about this privacy policy or our data practices, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
